import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Spinner } from 'reactstrap'
import MaterialReactTable from 'material-react-table'
import moment from 'moment'
import qs from 'qs'
import { useSelector } from 'react-redux'
import { InfoOutlined } from '@material-ui/icons'
import { ButtonLiberarPagamento } from '../../components/Modals/ModalContrFornInfo/ButtonLiberarPagamento'
import ModalContrFornInfo from '../../components/Modals/ModalContrFornInfo/ModalContrFornInfo'
import oikosApi from '../../services/oikos'
import { notifyError } from '../../utils/notify'

const rowsOf = (res) => (Array.isArray(res?.data) ? res.data : [])

const formatMoney = (value) => (
  (Number(value) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
)


const parteKey = (row) => {
  const tab = String(row?.contrForn_parteTab || '').trim().toLowerCase()
  const id = Number(row?.contrForn_parteId) || 0
  if ((tab !== 'pf' && tab !== 'pj') || !id) return ''
  return `${tab}:${id}`
}

const contratoFromRow = (row) => ({
  contrForn_id: row.contrForn_id,
  contrForn_desc: row.contrForn_desc,
  contrForn_num: row.contrForn_num,
  contrFornStatus_id: row.contrFornStatus_id,
  contrFornTp_id: row.contrFornTp_id,
  meioPgto_id: row.meioPgto_id,
  conta_id: row.conta_id,
  orcItem_id: row.orcItem_id,
  contrForn_parteTab: row.contrForn_parteTab,
  contrForn_parteId: row.contrForn_parteId,
  pixTp_id: row.pixTp_id,
  contrForn_chavePix: row.contrForn_chavePix,
  contrForn_vlrFixo: row.contrForn_vlrFixo,
  contrForn_dtPgtoFixa: row.contrForn_dtPgtoFixa,
})

const dateOnly = (value) => String(value || '').slice(0, 10)

const formatDate = (value) => {
  const dt = dateOnly(value)
  return dt ? moment(dt, 'YYYY-MM-DD').format('DD/MM/YYYY') : ''
}

const contratoTexto = (row) => {
  const num = row.contrForn_num ? `${row.contrForn_num} — ` : ''
  return `${num}${row.contrForn_desc || 'Sem descrição'}`
}

const parcelaTexto = (row) => {
  const num = row.contrFornEvento_num ? `${row.contrFornEvento_num} — ` : ''
  return `${num}${row.contrFornEvento_desc || 'Sem descrição'}`
}

const buildDisplayRows = (rows) => {
  const groups = new Map()
  rows.forEach((row) => {
    const key = String(row.contrForn_id || row.contrFornEvento_id)
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(row)
  })

  const out = []
  groups.forEach((parcelas) => {
    const sorted = [...parcelas].sort((a, b) => (
      dateOnly(a.contrFornEvento_dtPrev).localeCompare(dateOnly(b.contrFornEvento_dtPrev))
    ))
    if (sorted.length === 1) {
      out.push({ ...sorted[0], tipo: 'parcela' })
      return
    }
    const base = sorted[0]
    const datas = sorted.map((item) => dateOnly(item.contrFornEvento_dtPrev)).filter(Boolean)
    out.push({
      tipo: 'grupo',
      contrFornEvento_id: `grupo-${base.contrForn_id}`,
      contrForn_id: base.contrForn_id,
      contrForn_num: base.contrForn_num,
      contrForn_desc: base.contrForn_desc,
      contrFornStatus_id: base.contrFornStatus_id,
      contrFornTp_id: base.contrFornTp_id,
      meioPgto_id: base.meioPgto_id,
      conta_id: base.conta_id,
      orcItem_id: base.orcItem_id,
      contrForn_parteTab: base.contrForn_parteTab,
      contrForn_parteId: base.contrForn_parteId,
      pixTp_id: base.pixTp_id,
      contrForn_chavePix: base.contrForn_chavePix,
      contrForn_vlrFixo: base.contrForn_vlrFixo,
      contrForn_dtPgtoFixa: base.contrForn_dtPgtoFixa,
      contratado_nomePf: base.contratado_nomePf,
      contratado_nomePj: base.contratado_nomePj,
      centroDeCusto: base.centroDeCusto,
      contrForn_dtVigFim: base.contrForn_dtVigFim,
      qtdParcelas: sorted.length,
      somaVlr: sorted.reduce((total, item) => total + (Number(item.contrFornEvento_vlr) || 0), 0),
      dtPrevMin: datas[0] || '',
      dtPrevMax: datas[datas.length - 1] || '',
      parcelas: sorted,
    })
  })

  return out.sort((a, b) => {
    const da = a.tipo === 'grupo' ? a.dtPrevMin : dateOnly(a.contrFornEvento_dtPrev)
    const db = b.tipo === 'grupo' ? b.dtPrevMin : dateOnly(b.contrFornEvento_dtPrev)
    return da.localeCompare(db)
  })
}

const fetchByIds = async (table, idField, ids, extraData = {}, fields = {}) => {
  const unique = [...new Set(ids.map((id) => Number(id)).filter(Boolean))]
  const out = []
  for (let i = 0; i < unique.length; i += 80) {
    const slice = unique.slice(i, i + 80)
    const res = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table,
      data: { ...extraData, [idField]: `!IN!(${slice.join(',')})` },
      fields,
    })}`)
    out.push(...rowsOf(res))
  }
  return out
}

const CONTRATO_NOME_FIELDS = {
  contrForn_id: 'contrForn_id',
  '(SELECT pf_nomeExibicao FROM pf WHERE pf.pf_id = contrForn.contrForn_parteId AND contrForn.contrForn_parteTab = "pf" LIMIT 1)': 'contratado_nomePf',
  '(SELECT pj_razaosocial FROM pj WHERE pj.pj_id = contrForn.contrForn_parteId AND contrForn.contrForn_parteTab = "pj" LIMIT 1)': 'contratado_nomePj',
  '(SELECT nivel2 FROM viewRubr_OrcItem WHERE orcItem_id = contrForn.orcItem_id LIMIT 1)': 'centroDeCusto',
  '(SELECT nivel2_id FROM viewRubr_OrcItem WHERE orcItem_id = contrForn.orcItem_id LIMIT 1)': 'centroDeCustoRubr_id',
}

const buildPendingEventoSql = ({ today, rangeStart, rangeEnd, pfId, canSeeAll, projId }) => {
  const ownerFilter = canSeeAll ? '' : ` AND c.pf_id = ${Number(pfId) || 0}`
  const projFilter = projId
    ? ` AND c.orcItem_id IN (SELECT orcItem_id FROM orcItem WHERE orcRubr_id IN (SELECT id_oikos FROM viewOrcRubr_taxonomia2 WHERE nivel3_id = ${projId}))`
    : ''

  return `(SELECT e.contrFornEvento_id FROM contrFornEvento e INNER JOIN contrForn c ON c.contrForn_id = e.contrForn_id WHERE e.contrFornEvento_ativo = 1 AND c.contrForn_ativo = 1 AND c.contrFornStatus_id = 2 AND (e.contrFornEvento_aprovLiberado IS NULL OR e.contrFornEvento_aprovLiberado = 0) AND e.contrFornEvento_dtPrev IS NOT NULL AND DATE(e.contrFornEvento_dtPrev) > '1900-01-01' AND (DATE(e.contrFornEvento_dtPrev) < '${today}' OR (DATE(e.contrFornEvento_dtPrev) >= '${rangeStart}' AND DATE(e.contrFornEvento_dtPrev) <= '${rangeEnd}'))${ownerFilter}${projFilter})`
}

const CountBadge = ({ count, tone = "neutral" }) => {
  const filled = tone === "danger" || count > 0
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 22,
        height: 22,
        marginLeft: 8,
        padding: "0 6px",
        borderRadius: 999,
        background: tone === "onSolid" ? "#fff" : (tone === "danger" ? "#dc3545" : (filled ? "#495057" : "#e9ecef")),
        color: tone === "onSolid" ? "#212529" : (filled ? "#fff" : "#495057"),
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1,
      }}
    >
      {count}
    </span>
  )
}

const wrapCellSx = {
  whiteSpace: "normal",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  textOverflow: "clip",
  verticalAlign: "top",
}

const ParcelaSection = ({ title, hint, rows, columns, renderDetailPanel }) => {
  const totalParcelas = rows.reduce((total, row) => total + (row.tipo === 'grupo' ? row.qtdParcelas : 1), 0)
  const totalValor = rows.reduce((total, row) => (
    total + (row.tipo === 'grupo' ? Number(row.somaVlr) || 0 : Number(row.contrFornEvento_vlr) || 0)
  ), 0)
  const hasGrupo = rows.some((row) => row.tipo === 'grupo')
  return (
  <section className="mb-4">
    <h5 className="mb-1">
      {title}
      <span className="text-muted" style={{ fontSize: '14px', fontWeight: 'normal' }}>
        {` (${totalParcelas} · ${formatMoney(totalValor)})`}
      </span>
    </h5>
    {hint ? (
      <p className="text-muted mb-2" style={{ fontSize: '13px' }}>{hint}</p>
    ) : null}
    {rows.length === 0 ? (
      <span className="d-inline-block py-2">Nenhuma parcela.</span>
    ) : (
      <MaterialReactTable
        columns={columns}
        data={rows}
        getRowId={(row) => String(row.contrFornEvento_id)}
        initialState={{
          density: 'compact',
          pagination: { pageIndex: 0, pageSize: 20 },
          sorting: [{ id: 'contrFornEvento_dtPrev', desc: false }],
        }}
        enableGrouping={false}
        enableTopToolbar
        enableGlobalFilter
        enableToolbarInternalActions={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableColumnFilters={false}
        enableFilters={false}
        enablePagination={rows.length > 20}
        positionGlobalFilter="left"
        muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
        muiTableBodyCellProps={{ sx: { fontSize: '13px', ...wrapCellSx } }}
        muiTableHeadCellProps={{
          sx: { fontSize: '12px', fontWeight: 'normal', padding: '8px', ...wrapCellSx },
        }}
        muiTablePaginationProps={{
          sx: { '.MuiTablePagination-displayedRows': { fontSize: '12px' }, fontSize: '12px' },
          labelRowsPerPage: <span style={{ fontSize: '12px' }}>Linhas por página</span>,
          rowsPerPageOptions: [20, 50, 100],
        }}
        enableExpanding={hasGrupo}
        renderDetailPanel={hasGrupo ? renderDetailPanel : undefined}
        muiExpandButtonProps={({ row }) => ({
          sx: { display: row.original.tipo === 'grupo' ? 'inline-flex' : 'none' },
          title: 'Ver parcelas',
        })}
        muiTableBodyRowProps={({ row }) => ({
          sx: row.original.tipo === 'grupo' ? { backgroundColor: '#f8f9fa' } : undefined,
        })}
      />
    )}
  </section>
  )
}

const ParcelasLiberarTab = ({
  isActive = false,
  proj_orcRubrId,
  canSeeAll = false,
  selectedCcId,
}) => {
  const { user } = useSelector((state) => state.auth)
  const [month, setMonth] = useState(() => moment().startOf('month'))
  const [listaAtiva, setListaAtiva] = useState('mes')
  const [parcelas, setParcelas] = useState([])
  const [fornecedores, setFornecedores] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const reqRef = useRef(0)
  const parcelasRef = useRef([])
  const fornecedoresRef = useRef({})
  const callbacksRef = useRef({})

  parcelasRef.current = parcelas
  fornecedoresRef.current = fornecedores

  const monthStart = month.clone().startOf('month').format('YYYY-MM-DD')
  const monthEnd = month.clone().endOf('month').format('YYYY-MM-DD')
  const monthLabel = month.format('MMMM [de] YYYY')
  const pfId = Number(user?.pf_id) || 0
  const projId = Number(proj_orcRubrId) || 0

  const loadParcelas = useCallback(async () => {
    if (!pfId) return
    const reqId = reqRef.current + 1
    reqRef.current = reqId
    setIsLoading(true)

    try {
      const today = moment().format('YYYY-MM-DD')
      const res = await oikosApi.get(`/?${qs.stringify({
        action: 'get',
        table: 'contrFornEvento',
        data: {
          contrFornEvento_id: `!IN!${buildPendingEventoSql({
            today,
            rangeStart: moment(monthStart).subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
            rangeEnd: moment(monthEnd).add(1, 'month').endOf('month').format('YYYY-MM-DD'),
            pfId,
            canSeeAll,
            projId,
          })}`,
        },
        fields: {},
      })}`)

      let eventos = rowsOf(res)
      if (reqRef.current !== reqId) return

      const eventoIds = eventos.map((evento) => evento.contrFornEvento_id).filter(Boolean)
      const contratoIds = eventos.map((evento) => evento.contrForn_id).filter(Boolean)

      const [bdros, contratos] = await Promise.all([
        fetchByIds('bdro', 'bdro_tabId', eventoIds, { bdro_tabRef: 'contrFornEvento', bdro_ativo: 1 }).catch((error) => {
          console.error('Failed to fetch borderô das parcelas:', error)
          return []
        }),
        fetchByIds('contrForn', 'contrForn_id', contratoIds),
      ])
      if (reqRef.current !== reqId) return

      const bdroByEventoId = {}
      bdros
        .filter((bdro) => String(bdro.bdro_tabRef) === 'contrFornEvento' && Number(bdro.bdro_ativo) !== 0)
        .forEach((bdro) => {
          const tabId = String(bdro.bdro_tabId)
          const existing = bdroByEventoId[tabId]
          if (!existing || Number(bdro.bdro_id) > Number(existing.bdro_id)) {
            bdroByEventoId[tabId] = bdro
          }
        })

      const contratoById = Object.fromEntries(
        contratos.map((contrato) => [String(contrato.contrForn_id), contrato]),
      )

      const contratoIdsComNome = [...new Set(contratos.map((contrato) => contrato.contrForn_id).filter(Boolean))]
      const nomesRows = await fetchByIds('contrForn', 'contrForn_id', contratoIdsComNome, {}, CONTRATO_NOME_FIELDS)
      const nomesPorContrato = Object.fromEntries(
        nomesRows.map((row) => [String(row.contrForn_id), row]),
      )
      if (reqRef.current !== reqId) return

      eventos = eventos.map((evento) => {
        const contrato = contratoById[String(evento.contrForn_id)] || {}
        const nomes = nomesPorContrato[String(evento.contrForn_id)] || {}
        const bdro = bdroByEventoId[String(evento.contrFornEvento_id)]
        const bdroId = evento.bdro_id ?? bdro?.bdro_id ?? null
        const liberacaoAutomaticaComBordero = Number(evento.contrFornEvento_liberacaoAutomatica) === 1 && Number(bdroId)
        const dt = dateOnly(evento.contrFornEvento_dtPrev)
        return {
          ...evento,
          contrForn_desc: contrato.contrForn_desc,
          contrForn_num: contrato.contrForn_num,
          contrFornStatus_id: contrato.contrFornStatus_id ?? 2,
          contrFornTp_id: contrato.contrFornTp_id,
          meioPgto_id: evento.meioPgto_id ?? contrato.meioPgto_id,
          conta_id: contrato.conta_id,
          orcItem_id: contrato.orcItem_id,
          contrForn_parteTab: contrato.contrForn_parteTab,
          contrForn_parteId: contrato.contrForn_parteId,
          pixTp_id: contrato.pixTp_id,
          contrForn_chavePix: contrato.contrForn_chavePix,
          contrForn_vlrFixo: contrato.contrForn_vlrFixo,
          contrForn_dtPgtoFixa: contrato.contrForn_dtPgtoFixa,
          contrForn_dtVigFim: contrato.contrForn_dtVigFim || null,
          contratado_nomePf: nomes.contratado_nomePf,
          contratado_nomePj: nomes.contratado_nomePj,
          centroDeCusto: nomes.centroDeCusto,
          centroDeCustoRubr_id: nomes.centroDeCustoRubr_id,
          bdro_id: bdroId,
          bdro_dtVenc: bdro?.bdro_dtVenc ?? null,
          bdro_dtQuit: bdro?.bdro_dtQuit ?? null,
          contrFornEvento_aprovLiberado: liberacaoAutomaticaComBordero ? 1 : evento.contrFornEvento_aprovLiberado,
          situacao: dt < today ? 'atraso' : (dt >= monthStart && dt <= monthEnd ? 'mes' : 'vizinho'),
        }
      }).filter((evento) => Number(evento.contrFornEvento_aprovLiberado) !== 1)

      const pfIds = []
      const pjIds = []
      eventos.forEach((evento) => {
        const key = parteKey(evento)
        if (!key) return
        if (key.startsWith('pf:')) pfIds.push(key.slice(3))
        else pjIds.push(key.slice(3))
      })

      const [pfs, pjs] = await Promise.all([
        fetchByIds('pf', 'pf_id', pfIds),
        fetchByIds('pj', 'pj_id', pjIds),
      ])
      if (reqRef.current !== reqId) return

      const nextFornecedores = {}
      pfs.forEach((pf) => { nextFornecedores[`pf:${pf.pf_id}`] = pf })
      pjs.forEach((pj) => { nextFornecedores[`pj:${pj.pj_id}`] = pj })

      setFornecedores(nextFornecedores)
      setParcelas(eventos)
    } catch (error) {
      console.error(error)
      if (reqRef.current !== reqId) return
      notifyError('Não foi possível carregar as parcelas a liberar.')
      setParcelas([])
    } finally {
      if (reqRef.current === reqId) setIsLoading(false)
    }
  }, [canSeeAll, monthEnd, monthStart, pfId, projId])

  useEffect(() => {
    if (!isActive) return
    loadParcelas()
  }, [isActive, loadParcelas])

  const updateOneEvento = useCallback(async (eventoId) => {
    const res = await oikosApi.get(`/?${qs.stringify({
      action: 'get',
      table: 'contrFornEvento',
      data: {
        contrFornEvento_id: eventoId,
        contrFornEvento_ativo: 1,
      },
      fields: {},
    })}`)
    const updated = rowsOf(res)[0]
    const current = parcelasRef.current
    if (!updated) return current
    const next = current.map((item) => (
      Number(item.contrFornEvento_id) === Number(eventoId)
        ? {
          ...item,
          contrFornEvento_dtPrev: updated.contrFornEvento_dtPrev || item.contrFornEvento_dtPrev,
          contrFornEvento_desc: updated.contrFornEvento_desc ?? item.contrFornEvento_desc,
          contrFornEvento_vlr: updated.contrFornEvento_vlr ?? item.contrFornEvento_vlr,
          contrFornEvento_pgtoPrazoDias: updated.contrFornEvento_pgtoPrazoDias ?? item.contrFornEvento_pgtoPrazoDias,
        }
        : item
    ))
    parcelasRef.current = next
    return next
  }, [])

  callbacksRef.current = {
    loadParcelas,
    updateOneEvento,
    setParcelas: (value) => {
      if (Array.isArray(value)) setParcelas(value)
    },
    setFornecedorForRow: (next) => {
      const tab = next?.pf_id ? 'pf' : 'pj'
      const id = next?.pf_id || next?.pj_id
      if (!id) return
      setFornecedores((prev) => ({ ...prev, [`${tab}:${id}`]: next }))
    },
    onParteUpdated: (nextContrForn) => {
      setParcelas((prev) => prev.map((item) => (
        Number(item.contrForn_id) === Number(nextContrForn?.contrForn_id)
          ? {
            ...item,
            contrForn_parteTab: nextContrForn.contrForn_parteTab,
            contrForn_parteId: nextContrForn.contrForn_parteId,
          }
          : item
      )))
    },
  }

  const LiberarAcao = ({ item }) => {
    if (!item || item.tipo === 'grupo') return null
    const key = parteKey(item)
    return (
      <ButtonLiberarPagamento
        fornecedor={fornecedoresRef.current[key] || {}}
        setFornecedor={callbacksRef.current.setFornecedorForRow}
        contrFornEventoData={item}
        contrFornData={contratoFromRow(item)}
        listaParcelas={parcelasRef.current}
        setListaParcelas={callbacksRef.current.setParcelas}
        getEventos={callbacksRef.current.loadParcelas}
        updateOneEvento={callbacksRef.current.updateOneEvento}
        onContrFornParteUpdated={callbacksRef.current.onParteUpdated}
      />
    )
  }

  const VerContrato = ({ item }) => (
    <span className="d-inline-flex align-items-center">
      <span>{contratoTexto(item)}</span>
      <span onClick={(event) => event.stopPropagation()} style={{ marginLeft: 2 }}>
        <ModalContrFornInfo
          triggerSize="small"
          triggerTitle="Ver dados do contrato"
          triggerIcon={<InfoOutlined style={{ fontSize: 18, color: '#6c757d' }} />}
          contrFornData={contratoFromRow(item)}
          updateOneEvento={callbacksRef.current.updateOneEvento}
        />
      </span>
    </span>
  )

  const columns = useMemo(() => [
    {
      accessorKey: 'contratado',
      header: 'Fornecedor',
      accessorFn: (row) => row.contratado_nomePj || row.contratado_nomePf || 'Sem favorecido',
      size: 180,
    },
    {
      accessorKey: 'contrato',
      header: 'Contrato',
      accessorFn: (row) => contratoTexto(row),
      size: 240,
      Cell: ({ row }) => <VerContrato item={row.original} />,
    },
    {
      accessorKey: 'parcela',
      header: 'Parcela',
      accessorFn: (row) => (row.tipo === 'grupo' ? `${row.qtdParcelas} parcelas` : parcelaTexto(row)),
      size: 200,
    },
    {
      accessorKey: 'contrFornEvento_dtPrev',
      header: 'Data prevista',
      accessorFn: (row) => (row.tipo === 'grupo' ? row.dtPrevMin : dateOnly(row.contrFornEvento_dtPrev)),
      Cell: ({ row }) => {
        if (row.original.tipo === 'grupo') {
          const { dtPrevMin, dtPrevMax } = row.original
          if (!dtPrevMin) return ''
          if (dtPrevMin === dtPrevMax) return formatDate(dtPrevMin)
          return `${formatDate(dtPrevMin)} a ${formatDate(dtPrevMax)}`
        }
        return formatDate(row.original.contrFornEvento_dtPrev)
      },
      size: 150,
    },
    {
      accessorKey: 'contrFornEvento_vlr',
      header: 'Valor',
      accessorFn: (row) => (row.tipo === 'grupo' ? row.somaVlr : Number(row.contrFornEvento_vlr) || 0),
      Cell: ({ row }) => (
        row.original.tipo === 'grupo' ? (
          <span>
            {formatMoney(row.original.somaVlr)}
            <span className="d-block text-muted" style={{ fontSize: 11 }}>soma</span>
          </span>
        ) : formatMoney(row.original.contrFornEvento_vlr)
      ),
      size: 130,
    },
    {
      accessorKey: 'contrForn_dtVigFim',
      header: 'Fim de vigência',
      accessorFn: (row) => dateOnly(row.contrForn_dtVigFim),
      Cell: ({ row }) => {
        const dt = dateOnly(row.original.contrForn_dtVigFim)
        if (!dt) return '—'
        const vencido = dt < moment().format('YYYY-MM-DD')
        return (
          <span style={{ color: vencido ? '#b02a37' : 'inherit', fontWeight: vencido ? 600 : 400 }}>
            {formatDate(dt)}
            {vencido ? ' · vencido' : ''}
          </span>
        )
      },
      size: 150,
    },
    {
      accessorKey: 'centroDeCusto',
      header: 'Centro de custo',
      accessorFn: (row) => row.centroDeCusto || '',
      size: 160,
    },
    {
      id: 'acoes',
      header: 'Ações',
      enableSorting: false,
      size: 110,
      Cell: ({ row }) => <LiberarAcao item={row.original} />,
    },
  ], [])

  const renderDetailPanel = ({ row }) => {
    if (row.original.tipo !== 'grupo') return null
    return (
      <div style={{ padding: '4px 12px 12px 36px' }}>
        <table className="w-100" style={{ fontSize: 13 }}>
          <thead>
            <tr style={{ color: '#6c757d', textAlign: 'left' }}>
              <th style={{ fontWeight: 500, padding: '6px 8px' }}>Parcela</th>
              <th style={{ fontWeight: 500, padding: '6px 8px' }}>Data prevista</th>
              <th style={{ fontWeight: 500, padding: '6px 8px' }}>Valor</th>
              <th style={{ fontWeight: 500, padding: '6px 8px' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {row.original.parcelas.map((parcela) => (
              <tr key={parcela.contrFornEvento_id} style={{ borderTop: '1px solid #eee' }}>
                <td style={{ padding: '8px', whiteSpace: 'normal', wordBreak: 'break-word' }}>{parcelaTexto(parcela)}</td>
                <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{formatDate(parcela.contrFornEvento_dtPrev)}</td>
                <td style={{ padding: '8px', whiteSpace: 'nowrap' }}>{formatMoney(parcela.contrFornEvento_vlr)}</td>
                <td style={{ padding: '8px' }}><LiberarAcao item={parcela} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const visible = selectedCcId
    ? parcelas.filter((row) => String(row.centroDeCustoRubr_id) === String(selectedCcId))
    : parcelas
  const today = moment().format('YYYY-MM-DD')
  const countInMonth = (target) => {
    const start = target.clone().startOf('month').format('YYYY-MM-DD')
    const end = target.clone().endOf('month').format('YYYY-MM-DD')
    return visible.filter((row) => {
      const dt = dateOnly(row.contrFornEvento_dtPrev)
      return dt >= start && dt <= end && dt >= today
    }).length
  }
  const emAtraso = visible.filter((row) => row.situacao === 'atraso')
  const noMes = visible.filter((row) => row.situacao === 'mes')
  const linhas = buildDisplayRows(listaAtiva === 'atraso' ? emAtraso : noMes)
  const hint = listaAtiva === 'atraso'
    ? 'Data prevista anterior a hoje. A liberação pede uma nova data antes de anexar a evidência.'
    : (monthEnd < today
      ? 'As parcelas deste mês que não foram liberadas aparecem em atraso.'
      : 'Parcelas deste mês que ainda não foram liberadas.')

  return (
    <div className="pt-3">
      <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 8 }}>
        <Button color={listaAtiva === 'atraso' ? 'danger' : 'light'} onClick={() => setListaAtiva('atraso')}>
          Em atraso
          <CountBadge count={emAtraso.length} tone={listaAtiva === 'atraso' ? 'onSolid' : 'danger'} />
        </Button>
        <Button color="light" onClick={() => { setListaAtiva('mes'); setMonth((prev) => prev.clone().subtract(1, 'month')) }}>
          Mês anterior
          <CountBadge count={countInMonth(month.clone().subtract(1, 'month'))} />
        </Button>
        <Button
          color={listaAtiva === 'mes' ? 'primary' : 'light'}
          style={{ textTransform: 'capitalize', fontWeight: 700 }}
          onClick={() => setListaAtiva('mes')}
        >
          {monthLabel}
          <CountBadge count={noMes.length} tone={listaAtiva === 'mes' ? 'onSolid' : 'neutral'} />
        </Button>
        <Button color="light" onClick={() => { setListaAtiva('mes'); setMonth((prev) => prev.clone().add(1, 'month')) }}>
          Próximo mês
          <CountBadge count={countInMonth(month.clone().add(1, 'month'))} />
        </Button>
        {isLoading && <Spinner size="sm" color="primary" className="ml-3" />}
      </div>

      {isLoading && parcelas.length === 0 ? (
        <div className="d-flex align-items-center py-3">
          <Spinner size="sm" color="primary" className="mr-2" />
          <span>Carregando parcelas...</span>
        </div>
      ) : (
        <ParcelaSection
          title={listaAtiva === 'atraso' ? 'Em atraso' : `A liberar em ${monthLabel}`}
          hint={hint}
          rows={linhas}
          columns={columns}
          renderDetailPanel={renderDetailPanel}
        />
      )}
    </div>
  )
}

export default ParcelasLiberarTab
